import { useState, useRef, useEffect } from 'react';
import { Trash2, Copy, Image as ImageIcon, Calendar, HardDrive, Layers, Upload, Loader2, Plus, Pencil, X } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { useMedia, useDeleteMedia, useUpdateMedia } from '../hooks/useMedia';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { mediaService } from '../services/media';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import type { Media as MediaType, ResponsiveImageVersion } from '../types/entities';

interface PendingUpload {
  file: File;
  preview: string;
  title: string;
  alt: string;
}

export default function Media() {
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingMedia, setEditingMedia] = useState<MediaType | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editAlt, setEditAlt] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, refetch } = useMedia(page, 24);
  const deleteMedia = useDeleteMedia();
  const updateMedia = useUpdateMedia();

  const handleEdit = (media: MediaType) => {
    setEditingMedia(media);
    setEditTitle(media.title || '');
    setEditAlt(media.alt || '');
  };

  const handleSaveEdit = async () => {
    if (!editingMedia) return;
    await updateMedia.mutateAsync({
      id: editingMedia.id,
      data: { title: editTitle || undefined, alt: editAlt || undefined },
    });
    setEditingMedia(null);
  };

  // Preparer les fichiers pour l'upload (ouvre le modal de métadonnées)
  const handlePrepareUpload = (files: FileList) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Veuillez sélectionner des images');
      return;
    }

    const newPending: PendingUpload[] = imageFiles
      .filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} dépasse 5MB`);
          return false;
        }
        return true;
      })
      .map(file => ({
        file,
        preview: URL.createObjectURL(file),
        title: '',
        alt: '',
      }));

    if (newPending.length > 0) {
      setPendingUploads(newPending);
    }
  };

  // Nettoyer les URLs de preview quand le modal se ferme
  useEffect(() => {
    return () => {
      pendingUploads.forEach(p => URL.revokeObjectURL(p.preview));
    };
  }, [pendingUploads]);

  // Mettre à jour les métadonnées d'un fichier pending
  const updatePendingMetadata = (index: number, field: 'title' | 'alt', value: string) => {
    setPendingUploads(prev => prev.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    ));
  };

  // Supprimer un fichier de la liste pending
  const removePendingUpload = (index: number) => {
    setPendingUploads(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Annuler tous les uploads pending
  const cancelPendingUploads = () => {
    pendingUploads.forEach(p => URL.revokeObjectURL(p.preview));
    setPendingUploads([]);
  };

  // Uploader les fichiers avec leurs métadonnées
  const handleConfirmUpload = async () => {
    if (pendingUploads.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < pendingUploads.length; i++) {
        const { file, title, alt } = pendingUploads[i];
        await mediaService.uploadImage(file, {
          title: title || undefined,
          alt: alt || undefined
        });
        setUploadProgress(Math.round(((i + 1) / pendingUploads.length) * 100));
      }

      toast.success(`${pendingUploads.length} image(s) uploadée(s)`);
      cancelPendingUploads();
      refetch();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handlePrepareUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handlePrepareUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteMedia.mutateAsync(deleteId);
    setDeleteId(null);
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiée dans le presse-papier');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const parseResponsiveUrls = (responsiveUrls: string | null): ResponsiveImageVersion[] => {
    if (!responsiveUrls) return [];
    try {
      return JSON.parse(responsiveUrls);
    } catch {
      return [];
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Bibliothèque Média</h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
              Gérez vos images et fichiers uploadés
            </p>
          </div>
          <div className="flex items-center gap-4">
            {data && (
              <div className="text-sm text-gray-500">
                {data.pagination.total} média{data.pagination.total > 1 ? 's' : ''}
              </div>
            )}
            <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="w-full sm:w-auto">
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadProgress}%
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Ajouter des images</span>
                  <span className="sm:hidden">Ajouter</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
            ${isUploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">
            <span className="font-medium text-blue-600 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              Cliquez pour sélectionner
            </span>
            {' ou glissez-déposez vos images ici'}
          </p>
          <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP jusqu'à 5MB • Versions responsives générées automatiquement</p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Media Grid */}
        {data && data.data.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun média</h3>
            <p className="mt-2 text-sm text-gray-500">
              Commencez par uploader des images dans vos contenus
            </p>
          </div>
        )}

        {data && data.data.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {data.data.map((media: MediaType) => (
              <div
                key={media.id}
                className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Image Preview */}
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  <img
                    src={media.thumbnailUrl || media.cdnUrl}
                    alt={media.filename}
                    className="w-full h-full object-cover"
                  />

                  {/* Overlay with Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleEdit(media)}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                      title="Modifier les métadonnées"
                    >
                      <Pencil className="h-4 w-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleCopyUrl(media.cdnUrl)}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                      title="Copier l'URL"
                    >
                      <Copy className="h-4 w-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => window.open(media.cdnUrl, '_blank')}
                      className="p-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                      title="Ouvrir en grand"
                    >
                      <ImageIcon className="h-4 w-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => setDeleteId(media.id)}
                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Media Info */}
                <div className="p-3 space-y-1">
                  <p className="text-sm font-medium text-gray-900 truncate" title={media.title || media.filename}>
                    {media.title || media.filename}
                  </p>
                  {media.alt && (
                    <p className="text-xs text-gray-500 truncate" title={media.alt}>
                      Alt: {media.alt}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <HardDrive className="h-3 w-3" />
                      {formatFileSize(media.size)}
                    </span>
                    <span>{media.width} × {media.height}</span>
                  </div>
                  {(() => {
                    const versions = parseResponsiveUrls(media.responsiveUrls);
                    if (versions.length > 0) {
                      return (
                        <div className="flex items-center gap-1 text-xs text-blue-600">
                          <Layers className="h-3 w-3" />
                          {versions.length} version{versions.length > 1 ? 's' : ''} responsive
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Calendar className="h-3 w-3" />
                    {formatDate(media.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {page} sur {data.pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce média ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le fichier sera définitivement supprimé du stockage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Media Dialog */}
      <Dialog open={!!editingMedia} onOpenChange={() => setEditingMedia(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier les métadonnées</DialogTitle>
            <DialogDescription>
              Modifiez le titre et le texte alternatif de l'image pour le référencement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {editingMedia && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <img
                    src={editingMedia.thumbnailUrl || editingMedia.cdnUrl}
                    alt={editingMedia.alt || editingMedia.filename}
                    className="w-12 h-12 object-cover rounded flex-shrink-0"
                  />
                  <p className="text-xs text-gray-500">
                    {editingMedia.width} × {editingMedia.height} • {formatFileSize(editingMedia.size)}
                  </p>
                </div>
                <p className="text-xs text-gray-600 mt-2 break-all">
                  {editingMedia.filename}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-title">Nom de l'image (SEO)</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Ex: massage-relaxant-lourdes"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-alt">Texte alternatif (Alt)</Label>
              <Textarea
                id="edit-alt"
                value={editAlt}
                onChange={(e) => setEditAlt(e.target.value)}
                placeholder="Ex: Massage relaxant dans un cadre apaisant à Lourdes"
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter className="pt-4 border-t mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditingMedia(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSaveEdit}
              disabled={updateMedia.isPending}
            >
              {updateMedia.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Modal with Metadata */}
      <Dialog open={pendingUploads.length > 0} onOpenChange={(open) => !open && cancelPendingUploads()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Ajouter {pendingUploads.length} image{pendingUploads.length > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              Renseignez les métadonnées pour chaque image avant l'upload.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {pendingUploads.map((pending, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <img
                    src={pending.preview}
                    alt={pending.file.name}
                    className="w-16 h-16 object-cover rounded flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 break-all">
                      {pending.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(pending.file.size)}
                    </p>
                  </div>
                  {pendingUploads.length > 1 && (
                    <button
                      onClick={() => removePendingUpload(index)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Retirer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  <div>
                    <Label htmlFor={`title-${index}`} className="text-xs">Nom (SEO)</Label>
                    <Input
                      id={`title-${index}`}
                      value={pending.title}
                      onChange={(e) => updatePendingMetadata(index, 'title', e.target.value)}
                      placeholder="Ex: massage-relaxant-lourdes"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`alt-${index}`} className="text-xs">Texte alternatif (Alt)</Label>
                    <Input
                      id={`alt-${index}`}
                      value={pending.alt}
                      onChange={(e) => updatePendingMetadata(index, 'alt', e.target.value)}
                      placeholder="Ex: Massage relaxant à Lourdes"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="pt-4 border-t mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancelPendingUploads}
              disabled={isUploading}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleConfirmUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadProgress}%
                </>
              ) : (
                `Uploader ${pendingUploads.length} image${pendingUploads.length > 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
